import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {Box, Button, Stack, Tab, Tabs, Typography} from "@mui/material";
import {useNavigate} from "@tanstack/react-router";
import {type ReactElement, useContext, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import FeeProposals from "@/components/Fees/FeeProposals.tsx";
import InstructorFeesTable from "@/components/Fees/InstructorFeesTable.tsx";
import StatusHistoryModal from "@/components/Fees/StatusHistoryModal.tsx";
import FeeProposalRequestForm from "@/components/Forms/FeeProposalRequestForm.tsx";
import GlenSnackBar, {type BaseAlert} from "@/components/GlenSnackBar.tsx";
import {useFeeProposalsProvider} from "@/components/Providers/InstructorFeeProposalsProvider.tsx";
import {jwtInstructorContext, ROLE_FEE_ADMIN} from "@/components/Providers/JWTProvider.tsx";
import StandardPage from "@/components/StandardPage.tsx";
import TableTheme from "@/components/TableTheme.tsx";
import {useGetFeeRequestDefaults} from "@/hooks/Instructor/useGetFeeRequestDefaults.ts";
import type {MyFee} from "@/hooks/Instructor/useMyFees.ts";
import type {FeeRequest, FeeRequestDefaults} from "@/types/FeeRequest.ts";

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const feeRequestDefaultsError: BaseAlert = {
    severity: "error",
    message: "Unable to load the fee details for a new request.",
};

const TabPanel = (props: TabPanelProps) => {
    const {children, value, index, ...other} = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`fee-requests-tabpanel-${index}`}
            aria-labelledby={`fee-requests-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{pt: 3, width: "100%"}}>{children}</Box>}
        </div>
    );
};

const PENDING_REQUESTS_TAB = 0;
const APPROVED_FEES_TAB = 1;

const FeeProposalsPage = (): ReactElement => {
    const {user} = useContext(jwtInstructorContext);
    const navigate = useNavigate();
    const methods = useForm();
    const {feeProposals, showAlert, alertVariant, message, clearAlert} = useFeeProposalsProvider();
    const [tabValue, setTabValue] = useState(APPROVED_FEES_TAB);
    const [feeNotification, setFeeNotification] = useState<BaseAlert | null>(null);

    const [feeRequestDefaults, setFeeRequestDefaults] = useState<FeeRequestDefaults | undefined>();
    const [showStatusHistoryModal, setShowStatusHistoryModal] = useState<boolean>(false);
    const [showFeeProposalForm, setShowFeeProposalForm] = useState<boolean>(false);
    const [selectedFeeRequest, setSelectedFeeRequest] = useState<FeeRequest | null>(null);
    const getFeeRequestDefaults = useGetFeeRequestDefaults();
    const [makingFeeRequest, setMakingFeeRequest] = useState(false);

    const handleMakeFeeRequest = async (fee: MyFee) => {
        if (makingFeeRequest) {
            return;
        }

        setMakingFeeRequest(true);
        try {
            const response = await getFeeRequestDefaults(fee.id);

            if (!response?.ok) {
                setFeeNotification(feeRequestDefaultsError);
                return;
            }

            const defaults = (await response.json()) as FeeRequestDefaults;
            setSelectedFeeRequest(null);
            setFeeRequestDefaults({
                ...defaults,
                feeName: defaults.feeName ? `${defaults.feeName} (Duplicate)` : "",
            });
            setShowFeeProposalForm(true);
        } catch (e) {
            console.error("handleMakeFeeRequest", e);
            setFeeNotification(feeRequestDefaultsError);
        } finally {
            setMakingFeeRequest(false);
        }
    };

    if (user && !user.role.includes(ROLE_FEE_ADMIN)) {
        navigate({
            to: "/instructor/fees",
        }).catch(e => console.error(e));
    }

    return (
        <StandardPage maxWidth="xl">
            <FormProvider {...methods}>
                <div style={{width: "100%"}}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 3,
                            width: "100%",
                        }}
                    >
                        <Typography variant="h5" color="primary" sx={{fontWeight: 500}}>
                            My Fee Requests
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => {
                                setSelectedFeeRequest(null);
                                setFeeRequestDefaults(undefined);
                                setShowFeeProposalForm(true);
                            }}
                            sx={{
                                p: 1,
                            }}
                        >
                            <AddCircleOutlineIcon sx={{mr: 1}} />
                            Add Request
                        </Button>
                        {showFeeProposalForm && (
                            <FeeProposalRequestForm
                                show={showFeeProposalForm}
                                setShow={(show: boolean) => {
                                    setFeeRequestDefaults(undefined);
                                    setShowFeeProposalForm(show);
                                }}
                                feeRequest={selectedFeeRequest}
                                setFeeRequest={setSelectedFeeRequest}
                                feeRequestDefaults={feeRequestDefaults}
                                setFeeNotification={setFeeNotification}
                            />
                        )}
                    </Box>

                    <Box sx={{mb: 3}}>
                        <Tabs value={tabValue} onChange={(_, tab) => setTabValue(tab)}>
                            <Tab label="Pending Requests" />
                            <Tab label="Approved Fees" />
                        </Tabs>
                    </Box>
                    <TabPanel value={tabValue} index={PENDING_REQUESTS_TAB}>
                        <Stack sx={{mb: 3}}>
                            <TableTheme>
                                {feeProposals && (
                                    <FeeProposals
                                        feeRequests={feeProposals}
                                        setShowForm={setShowFeeProposalForm}
                                        setSelectedFeeRequest={setSelectedFeeRequest}
                                        setShowStatusHistoryModal={setShowStatusHistoryModal}
                                    />
                                )}
                            </TableTheme>
                        </Stack>
                    </TabPanel>
                    <TabPanel value={tabValue} index={APPROVED_FEES_TAB}>
                        <Stack sx={{mb: 3}}>
                            <TableTheme>
                                <InstructorFeesTable
                                    onMakeFeeRequest={fee => {
                                        void handleMakeFeeRequest(fee);
                                    }}
                                />
                            </TableTheme>
                        </Stack>
                    </TabPanel>
                </div>
            </FormProvider>
            {feeNotification && (
                <GlenSnackBar
                    show={true}
                    onClose={() => setFeeNotification(null)}
                    severity={feeNotification.severity}
                    message={feeNotification.message}
                />
            )}
            <GlenSnackBar
                onClose={clearAlert}
                show={showAlert}
                severity={alertVariant}
                message={message}
            />
            <StatusHistoryModal
                show={showStatusHistoryModal}
                handleClose={() => {
                    setShowStatusHistoryModal(false);
                    setSelectedFeeRequest(null);
                }}
                feeRequest={selectedFeeRequest}
            />
        </StandardPage>
    );
};

export default FeeProposalsPage;
